"use client";

/**
 * Sidebar Skills list: the skills the user has added or created.
 *
 * The "Create a skill" action + the library link live in the section HEADER
 * (rendered by AppSidebar) so all four sections' primary actions line up. This
 * component is just the live list.
 *
 * The list reads from `usePersonalSkills()` and updates live whenever a skill
 * is added, created, or removed (all flows go through the same context). The
 * built-in "create-a-skill" entry is the Create action, so it is filtered out
 * of the saved-skills list below.
 */

import Link from "next/link";
import { useParams, useRouter } from "next/navigation";
import { ExternalLink, Lock, MinusCircle, Zap } from "lucide-react";
import type { Skill } from "@/lib/mock/skills";
import { usePersonalSkills } from "@/contexts/PersonalSkillsContext";
import { useAgentLaunch } from "@/lib/agent-launch";
import {
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { MoreHorizontalIcon, TrashIcon } from "@/components/icons";
import { cn } from "@/lib/utils";
import { useEffect, useRef, useState } from "react";

const CREATE_SKILL_ID = "create-a-skill";

export function SidebarSkills() {
  const { skills, remove } = usePersonalSkills();
  const { setOpenMobile } = useSidebar();
  const launch = useAgentLaunch();
  const router = useRouter();
  const params = useParams();
  const activeId = typeof params?.id === "string" ? params.id : undefined;
  const [removeTarget, setRemoveTarget] = useState<Skill | null>(null);

  // The Create a Skill builder is an action pinned in AppSidebar, not a saved
  // skill, so never list it here.
  const savedSkills = skills.filter((s) => s.id !== CREATE_SKILL_ID);
  const idsKey = savedSkills.map((s) => s.id).join("|");

  // Shake (+ brief emerald flash) a skill the MOMENT it first appears, so a
  // just-created one grabs the eye. The list loads async (empty → existing
  // skills arrive a beat later), so we ABSORB any ids that show up within a
  // short window after mount as "the initial list" and never shake those. Only
  // additions that land later (a genuine create) shake — creating a skill
  // always takes far longer than this window.
  const seenRef = useRef<Set<string>>(new Set());
  const mountAtRef = useRef<number>(Date.now());
  const [shakingIds, setShakingIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    const ids = savedSkills.map((s) => s.id);
    const fresh = ids.filter((id) => !seenRef.current.has(id));
    if (fresh.length === 0) return;
    fresh.forEach((id) => seenRef.current.add(id));
    if (Date.now() - mountAtRef.current < 3000) return; // initial load, no shake
    setShakingIds(new Set(fresh));
    const t = setTimeout(() => setShakingIds(new Set()), 1100);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  const actionFor = (skill: Skill): "unadd" | "delete" => {
    if (skill.personal_action) return skill.personal_action;
    return skill.is_public ? "unadd" : "delete";
  };

  const runSkillNow = (skill: Skill) => {
    setOpenMobile(false);
    launch({
      skillId: skill.id,
      skillName: skill.name,
      skillRun: true,
      query: `Run the ${skill.name} skill.`,
    });
  };

  const openDetails = (skill: Skill) => {
    setOpenMobile(false);
    router.push(`/skills/${skill.id}`);
  };

  const confirmRemove = () => {
    if (!removeTarget) return;
    const target = removeTarget;
    const action = actionFor(target);
    setRemoveTarget(null);
    remove(target.id);
    if (action === "delete" && target.id === activeId) {
      router.push("/library?tab=skill");
    }
  };

  return (
    <>
      <SidebarMenu>
        {savedSkills.length === 0 ? (
          <div className="px-2 py-1.5 text-xs text-white/80">
            No skills yet. Click Create a skill to build your first one.
          </div>
        ) : (
          savedSkills.map((skill) => {
            const isActive = skill.id === activeId;
            const removeAction = actionFor(skill);
            const shaking = shakingIds.has(skill.id);
            return (
              <SidebarMenuItem
                key={skill.id}
                className={cn(
                  shaking &&
                    "skill-pop-in rounded-md bg-emerald-500/10 ring-1 ring-inset ring-emerald-400/40 transition-colors",
                )}
              >
                <SidebarMenuButton asChild isActive={isActive}>
                  <Link
                    href={`/skills/${skill.id}`}
                    onClick={() => setOpenMobile(false)}
                    className="flex items-center gap-2"
                    title={skill.name}
                  >
                    <span
                      className={cn(
                        "size-1.5 shrink-0 rounded-full transition-colors",
                        shaking
                          ? "bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.95)]"
                          : "bg-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.7)]",
                      )}
                    />
                    <span
                      className={cn(
                        "truncate min-w-0",
                        isActive
                          ? "text-[hsl(var(--sidebar-session-title-active))]"
                          : "text-[hsl(var(--sidebar-session-title))]",
                      )}
                    >
                      {skill.name}
                    </span>
                    {skill.undeletable && (
                      <Lock className="ml-auto size-3 shrink-0 text-white/70" />
                    )}
                  </Link>
                </SidebarMenuButton>

                <DropdownMenu modal={true}>
                  <DropdownMenuTrigger asChild>
                    <SidebarMenuAction
                      className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground mr-0.5"
                      showOnHover={!isActive}
                    >
                      <MoreHorizontalIcon />
                      <span className="sr-only">More</span>
                    </SidebarMenuAction>
                  </DropdownMenuTrigger>

                  <DropdownMenuContent side="bottom" align="end">
                    <DropdownMenuItem
                      className="cursor-pointer"
                      onSelect={() => runSkillNow(skill)}
                    >
                      <Zap className="size-4" />
                      <span>Run now</span>
                    </DropdownMenuItem>

                    <DropdownMenuItem
                      className="cursor-pointer"
                      onSelect={() => openDetails(skill)}
                    >
                      <ExternalLink className="size-4" />
                      <span>Open details</span>
                    </DropdownMenuItem>

                    {!skill.undeletable && (
                      <DropdownMenuItem
                        className={cn(
                          "cursor-pointer",
                          removeAction === "delete" &&
                            "text-destructive focus:bg-destructive/15 focus:text-destructive dark:text-red-500",
                        )}
                        onSelect={() => setRemoveTarget(skill)}
                      >
                        {removeAction === "delete" ? (
                          <TrashIcon />
                        ) : (
                          <MinusCircle className="size-4" />
                        )}
                        <span>
                          {removeAction === "delete" ? "Delete" : "Unadd"}
                        </span>
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </SidebarMenuItem>
            );
          })
        )}
      </SidebarMenu>

      <AlertDialog
        open={removeTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRemoveTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {removeTarget && actionFor(removeTarget) === "delete"
                ? "Delete this skill?"
                : "Unadd this skill?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {removeTarget && actionFor(removeTarget) === "delete"
                ? `This permanently deletes "${removeTarget.name}" from your skills. This cannot be undone.`
                : removeTarget
                  ? `This removes "${removeTarget.name}" from your sidebar. You can add it again from the library.`
                  : "This removes the skill from your sidebar."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRemove}>
              {removeTarget && actionFor(removeTarget) === "delete"
                ? "Delete"
                : "Unadd"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
